import { Component, OnInit, Input, signal, computed, effect, input, output, booleanAttribute, inject, ElementRef, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PricingDisplayProduct, PricingDisplayRegion } from '../../models';
import { PricingApiService } from '../../services';
import { PricingStoreService } from '../../services/pricing-store.service';
import { ResponsiveService } from '../../services/responsive.service';
// Avoid circular imports: import child components directly instead of barrel
import { ProductTabsComponent } from '../product-tabs/product-tabs.component';
import { PlanDisplayComponent } from '../plan-display/plan-display.component';
import { ComparisonModalComponent } from '../comparison-modal/comparison-modal.component';

/** Angular Elements passes null when an attribute is removed. */
const nullToEmpty = (value: string | null | undefined): string => value ?? '';

@Component({
  selector: 'pricing-app',
  standalone: true,
  imports: [
    CommonModule, 
    ProductTabsComponent, 
    PlanDisplayComponent, 
    ComparisonModalComponent
  ],
  templateUrl: './pricing-app.component.html',
  styleUrls: ['./pricing-app.component.scss']
})
export class PricingAppComponent implements OnInit {
  private readonly responsive = inject(ResponsiveService);
  private readonly elementRef = inject(ElementRef);
  
  /** Reactive mobile detection */
  readonly isMobile = this.responsive.isMobile;
  readonly isDesktop = this.responsive.isDesktop;
  
  // String inputs - work natively with web component attributes
  @Input() apiBaseUrl: string = '/api';
  @Input() defaultRegion: string = 'US';

  /**
   * Boolean inputs use booleanAttribute transform to handle
   * web component string attributes ("true"/"false") automatically.
   */
  showComparisonButton = input(true, { transform: booleanAttribute });
  allowRegionSelection = input(false, { transform: booleanAttribute });
  allowRangedValueSelection = input(false, { transform: booleanAttribute });

  /**
   * Optional embedding-host inputs (all additive; unset = behavior unchanged).
   * `products`/`plans` are comma-separated codes to show; `preselect-*` pick the initial
   * product/plan/tier(rangedValue)/region; `default-period` sets the billing toggle;
   * `cta-label` replaces the plan buttons' "Choose Plan" text.
   */
  products = input('', { transform: nullToEmpty });
  plans = input('', { transform: nullToEmpty });
  preselectProduct = input('', { transform: nullToEmpty });
  preselectPlan = input('', { transform: nullToEmpty });
  preselectTier = input('', { transform: nullToEmpty });
  preselectRegion = input('', { transform: nullToEmpty });
  defaultPeriod = input('', { transform: nullToEmpty });
  ctaLabel = input('', { transform: nullToEmpty });
  /** `top` puts the product tabs above the plans at every width; unset keeps the responsive layout. */
  tabsPosition = input('', { transform: nullToEmpty });
  readonly topTabs = computed(() => this.tabsPosition().trim().toLowerCase() === 'top');

  // Signal-based outputs for Angular v20
  readonly onPlanSelect = output<any>();
  readonly onRegionChange = output<PricingDisplayRegion>();
  readonly onRangedValueChange = output<number>();
  readonly onComparisonOpen = output<void>();

  // Signals for reactive state management (regions/local only)
  readonly availableRegions = signal<PricingDisplayRegion[]>([]);
  readonly showComparisonModal = signal(false);
  readonly modalOpenTimestamp = signal(0); // Timestamp to force unique modal instance
  readonly selectedRegion = computed(() => this.store.selectedRegion());
  readonly selectedRangedValue = computed(() => this.store.selectedRangedValue());

  // Computed properties for template usage - expose store signals as computed
  readonly selectedProductComputed = computed(() => this.store.selectedProduct());
  readonly productsComputed = computed(() => this.store.products());
  readonly loadingComputed = computed(() => this.store.loading());
  readonly errorComputed = computed(() => this.store.error());

  readonly availableRegionsComputed = computed(() => {
    const allRegions = this.store.products().flatMap(product => product.regions);
    return allRegions.filter((region, index, self) => 
      index === self.findIndex(r => r.code === region.code)
    );
  });

  readonly hasProducts = computed(() => this.store.products().length > 0);
  readonly hasError = computed(() => !!this.store.error());
  readonly showRangedValueSelector = computed(() => 
    this.allowRangedValueSelection() && this.selectedProductComputed()?.rangedValue
  );

  private pricingLoadedEmitted = false;
  /** The preselected tier applies once, to the preselected (or first) product. */
  private preselectTierConsumed = false;

  constructor(private apiService: PricingApiService, private store: PricingStoreService) {
    // Mirror host inputs into the store (also called from ngOnInit, before data loads)
    effect(() => this.syncHostInputs());

    // Host changed preselect-product after load: switch the product tab
    effect(() => {
      const code = this.preselectProduct().trim();
      untracked(() => {
        if (code && code !== this.store.selectedProduct()?.code && this.store.products().some(p => p.code === code)) {
          this.store.selectProductByCode(code);
        }
      });
    });

    // Set/reset rangedValue when product changes
    effect(() => {
      const product = this.selectedProductComputed();
      
      // If product has rangedValue options, always reset to first option
      // This ensures consistent behavior when switching between products
      if (product?.rangedValue?.options && product.rangedValue.options.length > 0) {
        const options = product.rangedValue.options;
        let option = options[0];

        // Host-preselected tier wins once, for the preselected (or first) product
        const tier = Number(this.preselectTier());
        const preProduct = this.preselectProduct().trim();
        if (!this.preselectTierConsumed && this.preselectTier().trim() !== '' && (!preProduct || product.code === preProduct)) {
          this.preselectTierConsumed = true;
          option = options.find(o => o.value === tier) ?? option;
        }

        // Always set when product changes
        this.store.selectedRangedValue.set(option.value);
      }
    });

    // Dispatch pricing-loaded event when products are loaded
    effect(() => {
      const loading = this.store.loading();
      const products = this.store.products();
      const error = this.store.error();
      
      if (!loading && products.length > 0 && !error && !this.pricingLoadedEmitted) {
        this.pricingLoadedEmitted = true;
        // Reveal after browser has painted the fully-styled content
        requestAnimationFrame(() => {
          this.elementRef.nativeElement.classList.add('pricing-ready');
        });
        this.dispatchCustomEvent('pricing-loaded', {
          productCount: products.length,
          products: products.map(p => ({ code: p.code, name: p.name }))
        });
      }

      // Dispatch error event when store has an error (from product loading)
      if (!loading && error) {
        requestAnimationFrame(() => {
          this.elementRef.nativeElement.classList.add('pricing-ready');
        });
        this.dispatchCustomEvent('error', { message: error, source: 'products' });
      }
    });
  }

  ngOnInit() {
    this.syncHostInputs();
    this.loadInitialData();
  }

  private static codes(value: string): string[] {
    return value.split(',').map(c => c.trim()).filter(Boolean);
  }

  private syncHostInputs() {
    this.store.productFilter.set(PricingAppComponent.codes(this.products()));
    this.store.planFilter.set(PricingAppComponent.codes(this.plans()));
    this.store.preselectProductCode.set(this.preselectProduct().trim());
    this.store.preselectPlanCode.set(this.preselectPlan().trim());
    this.store.defaultPeriod.set(this.defaultPeriod().trim() === 'yearly' ? 'yearly' : 'monthly');
    this.store.ctaLabel.set(this.ctaLabel().trim());
  }

  private async loadInitialData() {
    this.store.loading.set(true);
    this.store.error.set(null);

    // Load regions first
    this.apiService.getRegions().subscribe({
      next: async (regions) => {
        // Set available regions signal
        this.availableRegions.set(regions);
        
        // Find and set default region
        // 0. Host-preselected region (preselect-region), if it exists
        const preRegion = this.preselectRegion().trim();
        let defaultRegion = preRegion ? regions.find(r => r.code === preRegion) : undefined;

        // 1. Try isDefault flag from API
        defaultRegion = defaultRegion ?? regions.find(r => r.isDefault);
        
        // 2. Try IP-based detection
        if (!defaultRegion) {
          const detectedCountryCode = await this.detectRegionByIP();
          if (detectedCountryCode) {
            defaultRegion = regions.find(r => r.code === detectedCountryCode);
          }
        }
        
        // 3. Fallback to first region
        if (!defaultRegion) {
          defaultRegion = regions[0] || null;
        }
        
        this.store.selectedRegion.set(defaultRegion);
        
        // Load products (API returns ALL pricing, no filtering needed)
        this.loadProducts();
      },
      error: (err) => {
        const errorMessage = 'Failed to load regions. Please try again later.';
        this.store.error.set(errorMessage);
        this.store.loading.set(false);
        console.error('Error loading regions:', err);
        this.dispatchCustomEvent('error', { message: errorMessage, source: 'regions' });
      }
    });
  }

  private loadProducts() {
    // Note: API returns ALL pricing data regardless of region/rangedValue
    // We just call once and do dynamic lookup in components
    this.store.loadProducts();
  }
  
  /**
   * Detect user's country by IP address
   */
  private async detectRegionByIP(): Promise<string | null> {
    try {
      const response = await fetch('https://api.country.is');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const json = await response.json();
      return json.country || null;
    } catch (error) {
      console.warn('Could not detect region by IP:', error);
      return null;
    }
  }

  onProductSelect(product: PricingDisplayProduct) {
    this.store.selectProductByCode(product.code);
    console.log('Product selected:', product);
    this.dispatchCustomEvent('product-changed', { code: product.code, name: product.name });
  }

  onRegionSelected(regionCode: string) {
    const region = this.availableRegions().find(r => r.code === regionCode);
    if (region) {
      this.store.selectedRegion.set(region);
      this.onRegionChange.emit(region);
      this.dispatchCustomEvent('region-changed', region);
      // No need to reload products - prices are looked up dynamically
    }
  }

  onRangedValueSelected(value: number | string) {
    // Ensure it's always a number
    const numericValue = typeof value === 'string' ? Number(value) : value;
    this.store.selectedRangedValue.set(numericValue);
    this.onRangedValueChange.emit(numericValue);
    // No need to reload products - prices are looked up dynamically
  }

  onPlanSelected(plan: any) {
    this.onPlanSelect.emit(plan);
    this.dispatchCustomEvent('plan-selected', {
      code: plan.code, // kept for existing listeners
      productCode: this.store.selectedProduct()?.code,
      planCode: plan.code,
      region: this.store.selectedRegion()?.code,
      rangedValue: this.store.selectedRangedValue(),
      period: plan.period ?? 'monthly'
    });
  }

  /** Dispatch custom event on host element for web component consumers */
  private dispatchCustomEvent(eventName: string, detail: any) {
    this.elementRef.nativeElement.dispatchEvent(
      new CustomEvent(eventName, { detail, bubbles: true, composed: true })
    );
  }

  onFeatureHighlight(feature: any) {
    // Handle feature highlight if needed (emit event or show tooltip)
  }

  openComparison() {
    // Guard: only open modal if there's a selected product
    if (!this.selectedProductComputed()) {
      console.warn('[PricingApp] Cannot open comparison - no product selected');
      return;
    }
    // Use timestamp to force unique modal instance
    const timestamp = Date.now();
    this.modalOpenTimestamp.set(timestamp);
    this.showComparisonModal.set(true);
    this.onComparisonOpen.emit();
    this.dispatchCustomEvent('comparison-opened', { productCode: this.selectedProductComputed()?.code });
  }

  closeComparison() {
    this.showComparisonModal.set(false);
  }

  refreshData() {
    this.loadInitialData();
  }

}
